// modal-gallery.js
import EmblaCarousel from "embla-carousel";

// Simple body scroll lock
function disableBodyScroll() {
  const prev = document.body.style.overflow;
  document.body.dataset.prevOverflow = prev || "";
  document.body.style.overflow = "hidden";
}
function enableBodyScroll() {
  document.body.style.overflow = document.body.dataset.prevOverflow || "";
  delete document.body.dataset.prevOverflow;
}

/**
 * ModalGallery
 * - Turns any images inside `container` with data-gallery-image="true" into a modal gallery.
 * - Uses a single modal root in the DOM: .js-modal-gallery__root (from the Twig partial).
 */
export default class ModalGallery {
  /**
   * @param {HTMLElement} container - Scope where to find gallery images
   * @param {object} options
   *   options.modalSelector: override modal root selector
   */
  constructor(container, options = {}) {
    this.container = container || document;
    this.modalSelector = options.modalSelector || ".js-modal-gallery__root";

    // Modal & UI refs
    this.modal = null;
    this.embla = null;
    this.emblaRoot = null;
    this.emblaViewport = null;
    this.emblaContainer = null;

    this.components = {
      caption: null,
      credit: null,
      countCurrent: null,
      countTotal: null,
      close: [],
      next: [],
      previous: [],
    };

    // State
    this.slides = [];
    this.currentIndex = 0;
    this.isOpen = false;
    this.modalOpener = null;

    // Preload cache
    this._imageCache = new Map();

    // Bind
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.next = this.next.bind(this);
    this.previous = this.previous.bind(this);

    // Init
    this.findModal();
    this.setupComponents();
    this.setupSlides();
  }

  findModal() {
    this.modal = document.querySelector(this.modalSelector);
    if (!this.modal) {
      console.warn("Modal root not found:", this.modalSelector);
    } else {
      this.modal.setAttribute("aria-hidden", "true");
      // Make the dialog programmatically focusable
      if (!this.modal.hasAttribute("tabindex"))
        this.modal.setAttribute("tabindex", "-1");
    }
  }

  setupComponents() {
    if (!this.modal) return;

    this.emblaRoot = this.modal.querySelector(".js-modal-gallery__embla");
    this.emblaViewport = this.emblaRoot?.querySelector(".embla__viewport");
    this.emblaContainer = this.emblaRoot?.querySelector(".embla__container");

    this.components.caption = this.modal.querySelector(
      ".js-modal-gallery__caption",
    );
    this.components.credit = this.modal.querySelector(
      ".js-modal-gallery__credit",
    );
    this.components.countCurrent = this.modal.querySelector(
      ".js-modal-gallery__count--current",
    );
    this.components.countTotal = this.modal.querySelector(
      ".js-modal-gallery__count--total",
    );

    this.components.close = [
      ...this.modal.querySelectorAll(".js-modal-gallery__close"),
    ];
    this.components.next = [
      ...this.modal.querySelectorAll(".js-modal-gallery__next"),
    ];
    this.components.previous = [
      ...this.modal.querySelectorAll(".js-modal-gallery__previous"),
    ];

    this.components.zonePrev = this.modal.querySelector(
      ".modal-gallery__zone--previous",
    );
    this.components.zoneNext = this.modal.querySelector(
      ".modal-gallery__zone--next",
    );
  }

  setupSlides() {
    const media = this.container.querySelectorAll(
      'img[data-gallery-image="true"], video[data-gallery-image="true"]',
    );
    if (!media.length) return;

    this.slides = Array.from(media).map((element, index) => {
      // mark clickable
      element.style.cursor = "zoom-in";
      element.setAttribute("data-gallery-index", index);
      element.addEventListener("click", () => this.open(index));

      const isVideo = element.tagName.toLowerCase() === "video";

      return {
        element: element,
        type: isVideo ? "video" : "image",
        src: element.dataset.src || element.src,
        srcset: element.dataset.srcset || element.srcset || "",
        sizes: element.dataset.sizes || element.sizes || "",
        alt: element.alt || "",
        caption: this.getCaption(element),
        credit: element.dataset.credit || "",
      };
    });
  }

  getCaption(media) {
    let caption = media.dataset.caption || "";

    if (!caption) {
      const figure = media.closest("figure");
      if (figure) {
        const figcaption = figure.querySelector("figcaption");
        if (figcaption && figcaption.innerHTML) {
          caption = figcaption.innerHTML;
        }
      }
    }

    return caption;
  }

  renderSlides() {
    if (!this.emblaContainer) return;
    this.emblaContainer.innerHTML = "";
    this.slides.forEach((s, i) => {
      const slideEl = document.createElement("div");
      slideEl.className =
        "embla__slide basis-full shrink-0 grow-0 min-w-0 flex items-center justify-center px-grid-margin lg:px-0";

      if (s.type === "video") {
        const video = document.createElement("video");
        video.src = s.src;
        video.alt = s.alt;
        video.className =
          "js-modal-gallery__asset w-full h-full object-contain";
        video.muted = true;
        video.controls = false;
        video.autoplay = true;
        video.preload = "metadata";
        video.loop = true;
        video.playsInline = true;
        video.loading = i === this.currentIndex ? "eager" : "lazy";

        slideEl.appendChild(video);
      } else {
        const image = document.createElement("img");
        image.className =
          "js-modal-gallery__asset w-full h-full object-contain";
        image.alt = s.alt;
        image.decoding = "async";
        image.loading = i === this.currentIndex ? "eager" : "lazy";
        if (s.srcset) image.srcset = s.srcset;
        if (s.sizes) image.sizes = s.sizes;
        image.src = s.src;

        slideEl.appendChild(image);
      }

      this.emblaContainer.appendChild(slideEl);
    });
  }

  handleKeyDown(evt) {
    if (!this.isOpen) return;
    switch (evt.key) {
      case "ArrowLeft":
        this.previous();
        break;
      case "ArrowRight":
        this.next();
        break;
      case "Escape":
        this.close();
        break;
    }
  }

  updateUI() {
    const s = this.slides[this.currentIndex];
    if (this.components.caption) {
      // Unescape HTML entities in the caption before displaying
      const unescapedCaption = s.caption
        ? s.caption
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&amp;/g, "&")
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
        : "";
      this.components.caption.innerHTML = unescapedCaption
        ? `<span>${unescapedCaption}</span>`
        : "";

      // Handle external links in the caption
      this.handleExternalLinks(this.components.caption);
    }
    if (this.components.credit) {
      this.components.credit.innerHTML = s.credit
        ? `<span>${s.credit}</span>`
        : "";

      // Handle external links in the credit
      this.handleExternalLinks(this.components.credit);
    }
    if (this.components.countCurrent) {
      this.components.countCurrent.textContent = String(this.currentIndex + 1);
    }
    if (this.components.countTotal) {
      this.components.countTotal.textContent = String(this.slides.length);
    }
  }

  handleExternalLinks(container) {
    if (!container) return;

    const links = container.querySelectorAll("a");
    links.forEach((link) => {
      try {
        const url = new URL(link.href);

        // If it's an external link, add the proper attributes
        if (window.location.origin !== url.origin) {
          link.setAttribute("target", "_blank");
          link.setAttribute("rel", "noopener noreferrer");
          link.setAttribute("data-external", "true");
        }
      } catch (e) {
        // Invalid URL, skip
      }
    });
  }

  preloadSlide(index) {
    if (!this.slides.length) return;
    if (index < 0 || index >= this.slides.length) return;
    if (this._imageCache.has(index)) return;

    const s = this.slides[index];
    if (!s?.src) return;

    if (s.type === "video") {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.controls = false;
      video.autoplay = true;
      video.src = s.src;
      this._imageCache.set(index, video);
    } else {
      const img = new Image();
      img.decoding = "async";
      if (s.sizes) img.sizes = s.sizes;
      if (s.srcset) img.srcset = s.srcset;
      img.src = s.src;
      this._imageCache.set(index, img);
    }
  }

  preloadNeighbors() {
    if (!this.isOpen || this.slides.length <= 1) return;
    const nextIdx = (this.currentIndex + 1) % this.slides.length;
    const prevIdx =
      (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.preloadSlide(nextIdx);
    this.preloadSlide(prevIdx);
  }

  open(index = 0) {
    if (this.isOpen || !this.modal || !this.slides.length) return;

    this.currentIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    this.isOpen = true;

    // Add/remove single-slide class based on slide count
    if (this.slides.length < 2) {
      this.modal.classList.add("modal-gallery--single-slide");
    } else {
      this.modal.classList.remove("modal-gallery--single-slide");
    }

    // listeners
    window.addEventListener("keydown", this.handleKeyDown);
    this.components.close.forEach((btn) =>
      btn.addEventListener("click", this.close),
    );
    this.components.next.forEach((btn) =>
      btn.addEventListener("click", this.next),
    );
    this.components.previous.forEach((btn) =>
      btn.addEventListener("click", this.previous),
    );

    if (this.components.zonePrev) {
      this.components.zonePrev.addEventListener("click", this.previous, {
        passive: true,
      });
    }
    if (this.components.zoneNext) {
      this.components.zoneNext.addEventListener("click", this.next, {
        passive: true,
      });
    }

    // render slides and mount Embla
    this.renderSlides();
    this.embla = EmblaCarousel(this.emblaViewport, {
      loop: this.slides.length > 1,
      align: "center",
      watchDrag: true,
    });

    // jump to requested
    this.embla.scrollTo(this.currentIndex, true);

    // sync on selection
    this.embla.on("select", () => {
      this.currentIndex = this.embla.selectedScrollSnap();
      this.updateUI();
      this.preloadNeighbors();
    });

    // show modal
    this.updateUI();
    this.preloadNeighbors();
    this.modal.setAttribute("aria-hidden", "false");
    this.modalOpener = document.activeElement;
    this.modal.focus();
    disableBodyScroll();
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;

    // Remove single-slide class when closing
    this.modal.classList.remove("modal-gallery--single-slide");

    window.removeEventListener("keydown", this.handleKeyDown);
    this.components.close.forEach((btn) =>
      btn.removeEventListener("click", this.close),
    );
    this.components.next.forEach((btn) =>
      btn.removeEventListener("click", this.next),
    );
    this.components.previous.forEach((btn) =>
      btn.removeEventListener("click", this.previous),
    );

    if (this.components.zonePrev) {
      this.components.zonePrev.removeEventListener("click", this.previous);
    }
    if (this.components.zoneNext) {
      this.components.zoneNext.removeEventListener("click", this.next);
    }

    if (this.embla) {
      this.embla.destroy();
      this.embla = null;
    }

    this.modal.setAttribute("aria-hidden", "true");
    enableBodyScroll();

    // restore focus
    if (this.modalOpener && this.modalOpener !== document.body) {
      this.modalOpener.focus();
      this.modalOpener = null;
    }
  }

  next() {
    if (this.embla) this.embla.scrollNext();
  }

  previous() {
    if (this.embla) this.embla.scrollPrev();
  }

  disconnect() {
    if (this.isOpen) this.close();
  }
}
